import React from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    Divider,
    Grid,
    MenuItem,
    TextField,
    Typography,
} from "@mui/material";
import { observer } from "mobx-react-lite";
import CartStore from "../../stores/CartStore";
import { priceFormatter } from "../../utils/helpers";
import ASCartRow from "../cart/ASCartRow";
import CartRow from "../cart/CartRow";
import DialogStore from "../../stores/DialogStore";
import NothingHereYet from "../NothingHereYet";
import SeatmapCartRow from "../cart/SeatmapCartRow";
import PackageCartRow from "../cart/PackageCartRow";
import InfoBubble from "../common/InfoBubble";
import InputField from "../form/InputField";
import { EcommerceEventList } from "../../models/Event";
import AuthStore from "../../stores/AuthStore";

type Props = {
    cartStore: CartStore;
    eventList: EcommerceEventList;
    disablePromoCode?: boolean;
};

const CheckoutCart = observer(
    ({ cartStore, eventList, disablePromoCode = false }: Props) => {
        const [promoCode, setPromoCode] = React.useState("");
        const [redemptionCode, setRedemptionCode] = React.useState("");

        const subtotal = cartStore.subtotal ?? 0;
        const authFee = cartStore.authFee ?? 0;
        const total = cartStore.total ?? 0;

        const showSubtotal = subtotal !== total;
        const hasItems = (cartStore.cart?.items.length || 0) > 0;

        const onClearCart = () => {
            if (cartStore?.quantity === 0) return;
            DialogStore.showAlert(
                "Clear Cart",
                "Are you sure you want to clear your cart? Some tickets may no longer be available to purchase.",
                () => cartStore?.clearCart(),
            );
        };

        return (
            <Box sx={{ my: 2 }}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Typography variant="h4" textAlign="center">
                            Your Cart
                        </Typography>
                    </Grid>
                    {cartStore.hasItems && (
                        <Grid item xs={12}>
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "flex-end",
                                }}
                            >
                                <Button
                                    variant="contained"
                                    color="error"
                                    disabled={cartStore.quantity === 0}
                                    onClick={onClearCart}
                                >
                                    Clear Cart
                                </Button>
                            </Box>
                        </Grid>
                    )}
                    {cartStore?.packageItemsGrouped.map((it) => (
                        <Grid
                            item
                            xs={12}
                            key={`${it.eventPackage.id}-${it.items
                                .map((it2) => it2.id)
                                .join("-")}`}
                        >
                            <PackageCartRow row={it} cartStore={cartStore} />
                        </Grid>
                    ))}
                    {cartStore?.seatmapItemsGrouped.map((it) => (
                        <Grid item xs={12} key={it.eventSeatmap.id}>
                            <SeatmapCartRow row={it} cartStore={cartStore} />
                        </Grid>
                    ))}
                    {cartStore.assignedSeatingItemsGrouped.map((it) => (
                        <Grid
                            item
                            xs={12}
                            key={it.items.map((it2) => it2.ticketId).join("-")}
                        >
                            <Card>
                                <CardContent>
                                    <Typography
                                        variant="body2"
                                        color="primaryText.main"
                                        fontWeight={600}
                                    >
                                        {it.department.name}
                                    </Typography>
                                    <Divider sx={{ my: 2 }} />
                                    <Grid container spacing={2}>
                                        {it.items.map((it2, idx2) => (
                                            <Grid
                                                item
                                                xs={12}
                                                key={it2.ticketId}
                                            >
                                                <ASCartRow
                                                    item={it2}
                                                    onRemove={() =>
                                                        cartStore.removeTicketsFromCart(
                                                            [it2.ticketId],
                                                        )
                                                    }
                                                    cartStore={cartStore}
                                                    onRemoveRedemptionCode={() => {
                                                        if (!it2.redemption)
                                                            return;
                                                        cartStore.removeRedemptionCode(
                                                            it2.redemption?.id,
                                                        );
                                                    }}
                                                />
                                                {idx2 !==
                                                    it.items.length - 1 && (
                                                    <Divider
                                                        sx={{
                                                            mt: 2,
                                                        }}
                                                    />
                                                )}
                                            </Grid>
                                        ))}
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                    {cartStore.gaItems.map((it) => (
                        <Grid item xs={12} key={it.ticketIds.join("-")}>
                            <CartRow
                                item={it}
                                onIncrement={() => {
                                    cartStore.incrementGaQuantity(
                                        it.department.id,
                                        it.comp,
                                        it.department,
                                        it.redemption?.id,
                                    );
                                }}
                                onDecrement={() => {
                                    cartStore.decrementGaQuantity(
                                        it.department.id,
                                        it.comp,
                                        it.department,
                                        it.redemption?.id,
                                    );
                                }}
                                onSetQuantity={(quantity) => {
                                    cartStore.setGaQuantity(
                                        it.department.id,
                                        it.comp,
                                        quantity,
                                        it.department,
                                        it.redemption?.id,
                                    );
                                }}
                                onRemove={() => {
                                    cartStore.setGaQuantity(
                                        it.department.id,
                                        it.comp,
                                        0,
                                        it.department,
                                        it.redemption?.id,
                                    );
                                }}
                                onRemoveRedemptionCode={() => {
                                    if (!it.redemption) return;
                                    cartStore.removeRedemptionCode(
                                        it.redemption?.id,
                                    );
                                }}
                            />
                        </Grid>
                    ))}
                    {!cartStore?.hasItems && (
                        <Grid item xs={12}>
                            <NothingHereYet message="Your cart is empty." />
                        </Grid>
                    )}
                </Grid>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ mb: 1 }}>
                    {cartStore?.shouldShowPromoCodeForm &&
                        !disablePromoCode && (
                            <form
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    if (!cartStore?.promoCode) {
                                        cartStore?.applyPromoCode(promoCode);
                                        setPromoCode("");
                                    } else {
                                        cartStore?.removePromoCode();
                                    }
                                }}
                            >
                                <Grid container spacing={1} sx={{ px: 1 }}>
                                    <Grid item xs={8}>
                                        <TextField
                                            label="Promo Code"
                                            fullWidth
                                            size="small"
                                            color="primary"
                                            value={
                                                cartStore?.promoCode ||
                                                promoCode
                                            }
                                            disabled={
                                                !!cartStore?.promoCode ||
                                                cartStore?.loading
                                            }
                                            onChange={(e) => {
                                                setPromoCode(e.target.value);
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Button
                                            variant="contained"
                                            color={
                                                cartStore?.promoCode
                                                    ? "error"
                                                    : "primary"
                                            }
                                            fullWidth
                                            type="submit"
                                            disabled={cartStore?.loading}
                                        >
                                            {cartStore?.promoCode
                                                ? "Clear"
                                                : "Apply"}
                                        </Button>
                                    </Grid>
                                </Grid>
                            </form>
                        )}
                    {cartStore?.shouldShowRedemptionCodeForm && (
                        <form
                            onSubmit={(e) => {
                                e.preventDefault();
                                if (!redemptionCode) return;
                                cartStore?.applyRedemptionCode(redemptionCode);
                                setRedemptionCode("");
                            }}
                            style={{ marginTop: 5 }}
                        >
                            <Grid container spacing={1} sx={{ px: 1 }}>
                                <Grid item xs={8}>
                                    <TextField
                                        label="Redemption Code"
                                        fullWidth
                                        size="small"
                                        color="primary"
                                        value={redemptionCode}
                                        disabled={cartStore?.loading}
                                        onChange={(e) => {
                                            setRedemptionCode(e.target.value);
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={4}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        fullWidth
                                        type="submit"
                                        disabled={
                                            !redemptionCode ||
                                            cartStore?.loading
                                        }
                                    >
                                        Apply
                                    </Button>
                                </Grid>
                            </Grid>
                        </form>
                    )}
                    <Grid container spacing={1} sx={{ my: 2, px: 2 }}>
                        <Grid item xs={12}>
                            <InputField
                                select
                                fullWidth
                                value={cartStore?.deliveryMethod?.id ?? ""}
                                onChange={(e) => {
                                    cartStore?.changeDeliveryMethod(
                                        parseInt(e.target.value, 10),
                                    );
                                }}
                                disabled={!cartStore?.hasItems}
                                SelectProps={{
                                    renderValue: (value) => {
                                        const dm =
                                            eventList.deliveryMethods.find(
                                                (it) => it.id === value,
                                            );
                                        return (
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    justifyContent:
                                                        "space-between",
                                                    width: "100%",
                                                }}
                                            >
                                                <div>{dm?.name}</div>
                                                <div>
                                                    {priceFormatter(
                                                        dm?.amount ?? 0,
                                                    )}
                                                </div>
                                            </Box>
                                        );
                                    },
                                }}
                            >
                                {eventList.deliveryMethods
                                    .filter(
                                        (it) =>
                                            !it.isBoxofficeOnly ||
                                            AuthStore.boxoffice,
                                    )
                                    .sort((a, b) => {
                                        if (a.id === 0) return 1;
                                        else if (a.amount === b.amount)
                                            return a.name.localeCompare(b.name);
                                        else return a.amount - b.amount;
                                    })
                                    .map((dm) => (
                                        <MenuItem
                                            value={dm.id}
                                            key={dm.id}
                                            sx={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                            }}
                                        >
                                            <div>{dm.name}</div>
                                            <div>
                                                {priceFormatter(dm.amount)}
                                            </div>
                                        </MenuItem>
                                    ))}
                            </InputField>
                        </Grid>
                        {authFee !== 0 && (
                            <>
                                <Grid item xs={8}>
                                    <Typography variant="body2">
                                        Transaction Fee
                                    </Typography>
                                </Grid>
                                <Grid item xs={4}>
                                    <Typography
                                        variant="body2"
                                        textAlign="right"
                                    >
                                        {priceFormatter(authFee)}
                                    </Typography>
                                </Grid>
                            </>
                        )}
                        {/* {cartStore?.deliveryMethod && (
                        <>
                            <Grid item xs={8}>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "3px",
                                    }}
                                >
                                    {cartStore.deliveryMethod.name}
                                </Typography>
                            </Grid>
                            <Grid item xs={4}>
                                <Typography variant="body2" textAlign="right">
                                    {priceFormatter(
                                        cartStore.deliveryMethod.amount,
                                    )}
                                </Typography>
                            </Grid>
                        </>
                    )} */}
                        {showSubtotal && (
                            <>
                                <Grid item xs={8}>
                                    <Typography variant="body2">
                                        Subtotal
                                    </Typography>
                                </Grid>
                                <Grid item xs={4}>
                                    <Typography
                                        variant="body2"
                                        textAlign="right"
                                    >
                                        {priceFormatter(subtotal)}
                                    </Typography>
                                </Grid>
                            </>
                        )}
                        {cartStore.taxes.map((it) => (
                            <>
                                <Grid item xs={8}>
                                    <Typography variant="body2">
                                        {it.name}
                                    </Typography>
                                </Grid>
                                <Grid item xs={4}>
                                    <Typography
                                        variant="body2"
                                        textAlign="right"
                                    >
                                        {priceFormatter(it.amount)}
                                    </Typography>
                                </Grid>
                            </>
                        ))}
                        <Grid item xs={8}>
                            <Typography fontWeight={900}>Total</Typography>
                        </Grid>
                        <Grid item xs={4} textAlign="right">
                            <Typography>{priceFormatter(total)}</Typography>
                        </Grid>
                    </Grid>
                </Box>
            </Box>
        );
    },
);

export default CheckoutCart;
